import { useState, useRef } from 'react';
import { Upload, X, ImageIcon, Loader2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { uploadPropertyImages, getThumbnailUrl } from '../services/api';
import { toast } from 'sonner';

const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export default function ImageUploadBox({ property, onImagesUpdated, onClose }) {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const [existingImgs, setExistingImgs] = useState(
    (property.images || []).filter(u => u && (u.startsWith('http') || u.startsWith('/api/')))
  );

  const removeExistingImage = async (index) => {
    const updated = existingImgs.filter((_, i) => i !== index);
    setExistingImgs(updated);
    try {
      await onImagesUpdated(property.id, updated);
      toast.success('Image removed');
    } catch {
      toast.error('Failed to remove image');
      setExistingImgs(existingImgs); // revert
    }
  };

  const addFiles = (newFiles) => {
    const validFiles = [];
    for (const f of newFiles) {
      if (!f.type.startsWith('image/')) {
        toast.error(`"${f.name}" is not an image file`);
        continue;
      }
      if (f.size > MAX_SIZE_BYTES) {
        toast.error(`"${f.name}" is ${(f.size / 1024 / 1024).toFixed(1)}MB — exceeds ${MAX_SIZE_MB}MB limit`);
        continue;
      }
      validFiles.push(f);
    }
    if (validFiles.length === 0) return;
    setFiles(prev => [...prev, ...validFiles]);
    validFiles.forEach(f => {
      const reader = new FileReader();
      reader.onload = (e) => setPreviews(prev => [...prev, { name: f.name, size: f.size, src: e.target.result }]);
      reader.readAsDataURL(f);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) addFiles(Array.from(e.dataTransfer.files));
  };

  const handleChange = (e) => {
    if (e.target.files?.length) addFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const result = await uploadPropertyImages(files);
      const newUrls = result.urls || [];
      if (newUrls.length > 0) {
        const allImages = [...existingImgs, ...newUrls];
        await onImagesUpdated(property.id, allImages);
        setUploaded(true);
        toast.success(`${newUrls.length} image${newUrls.length > 1 ? 's' : ''} uploaded successfully!`);
        setTimeout(() => onClose(), 1500);
      } else {
        toast.error('Upload failed — no images were saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="image-upload-modal">
      <div className="fixed inset-0 bg-black/80" onClick={onClose} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative w-full max-w-xl bg-[#1A1D24] border border-[#2A2F3A] text-white rounded-xl p-6 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-lg font-semibold">Upload Images</h2>
                <p className="text-[#A1A1AA] text-xs mt-0.5">{property.title}</p>
              </div>
              <button type="button" onClick={onClose} className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="close-image-upload">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Existing images */}
            {existingImgs.length > 0 && (
              <div className="mb-4">
                <p className="text-[#A1A1AA] text-xs mb-2">Current images ({existingImgs.length}) — click X to remove</p>
                <div className="flex flex-wrap gap-2">
                  {existingImgs.map((url, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-[#2A2F3A] group">
                      <img src={getThumbnailUrl(url, 100)} alt="" className="w-full h-full object-cover" loading="lazy" />
                      <button type="button" onClick={(e) => { e.stopPropagation(); removeExistingImage(i); }}
                        className="absolute inset-0 bg-black/0 group-hover:bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                        data-testid={`remove-existing-${i}`}>
                        <X className="h-4 w-4 text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Drop zone */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${dragActive ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#2A2F3A] hover:border-[#D4AF37]/40'}`}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              data-testid="image-drop-zone"
            >
              <input ref={inputRef} type="file" multiple accept="image/*" className="hidden" onChange={handleChange} data-testid="image-file-input" />
              <div className="flex flex-col items-center">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${dragActive ? 'bg-[#D4AF37]/20' : 'bg-[#2A2F3A]'}`}>
                  <Upload className={`h-6 w-6 ${dragActive ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'}`} />
                </div>
                <p className="text-white text-sm font-medium mb-1">
                  {dragActive ? 'Drop images here' : 'Click or drag images here'}
                </p>
                <p className="text-[#A1A1AA] text-xs">
                  JPG, PNG, WebP — any aspect ratio
                </p>
                <p className="text-[#D4AF37] text-xs font-medium mt-1">
                  Maximum {MAX_SIZE_MB}MB per image
                </p>
              </div>
            </div>

            {/* Previews */}
            {previews.length > 0 && (
              <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
                {previews.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-[#0B0C10] border border-[#2A2F3A] rounded-lg">
                    <img src={p.src} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{p.name}</p>
                      <p className="text-[#A1A1AA] text-xs">{(p.size / 1024).toFixed(0)} KB</p>
                    </div>
                    {!uploading && !uploaded && (
                      <button type="button" onClick={() => removeFile(i)} className="text-[#A1A1AA] hover:text-red-400 transition-colors flex-shrink-0" data-testid={`remove-preview-${i}`}>
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#2A2F3A]">
              <p className="text-[#A1A1AA] text-xs">
                {files.length > 0 ? `${files.length} file${files.length > 1 ? 's' : ''} selected` : 'No files selected'}
              </p>
              {uploaded ? (
                <Button disabled className="bg-emerald-600 text-white">
                  <Check className="h-4 w-4 mr-1" /> Uploaded!
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleUpload}
                  disabled={files.length === 0 || uploading}
                  className="btn-luxury"
                  data-testid="upload-images-btn"
                >
                  {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading...</> : <><Upload className="h-4 w-4 mr-1" /> Upload {files.length > 0 ? `${files.length} Image${files.length > 1 ? 's' : ''}` : ''}</>}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
